import { PaginationParams } from '@/lib/types';
import { api } from './api';
import { Clinic } from '@/models/clinic.model';

export interface  ClinicContext {
  clinic: Clinic;
  is_admin: boolean;
  is_practitioner: boolean;
  is_clinic_staff: boolean;
  role_permissions: string[];
}

export interface ClinicUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  clinic: {
    id: number;
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
  };
}

export interface Patient {
  id: number;
  first_name: string;
  middle_name?: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  gender: string;
  created_at: string;
}

export interface Practitioner {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  npi: string;
  dea: string;
  specialization: string;
  status: string;
  created_at: string;
}

export interface Transaction {
  id: number;
  amount: number;
  status: string;
  patient_name: string;
  practitioner_name: string;
  productName: string;
  clinicRevenue: number;
  created_at: string;
  processed_at: string;
}

export interface PaymentOverview {
  metrics: {
    totalRevenue: number;
    pendingSettlements: number;
    completedTransactions: number;
    averageTransaction: number;
    monthlyGrowth: number;
  };
  revenueData: Array<{
    date: string;
    amount: number;
  }>;
  dateRange: {
    start_date: string;
    end_date: string;
  };
}

// Dashboard types
export interface DashboardMetrics {
  totalPatients: number;
  activePractitioners: number;
  totalProducts: number;
  patientChange?: number;
  practitionerChange?: number;
  productChange?: number;
}

export interface DashboardGrowthData {
  month: string;
  patients: number;
  practitioners: number;
}

export interface DashboardActivity {
  id: string;
  type: 'patient' | 'practitioner' | 'prescription' | 'payment' | 'price_change' | 'staff';
  action: string;
  entity: string;
  amount?: number;
  timestamp: string;
}

export interface Prescription {
  id: number;
  patient: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
  };
  practitioner: {
    id: number;
    first_name: string;
    last_name: string;
  };
  status: string;
  created_at: string;
  medicationRequests: Array<{
    id: number;
    medication_name: string;
    dosage: string;
    quantity: number;
    refills: number;
  }>;
}

class ClinicService {
  // Get current clinic user info
  async getCurrentUser(): Promise<ClinicUser> {
    const response = await api.get('/clinic/me');
    return response.data;
  }

  // Get clinic patients with pagination
  async getPatients(
    page = 1,
    limit = 20,
    search = '',
    clinics = []
  ): Promise<{
    patients: Patient[];
    pagination: any;
  }> {
    const response = await api.post('/clinic/patients/list', {
      page,
      limit,
      search,
      clinics,
    });
    return response.data;
  }

  // Get patient dropdown for forms
  async getPatientDropdown(search = '', clinicId: string): Promise<{ patients: Patient[] }> {
    const response = await api.post('/clinic/patients/get-patient-dropdown', {
      search,
      clinic_id: clinicId,
    });
    return response.data;
  }

  // Get clinic practitioners
  async getPractitioners(
    clinicId: string,
    pagination: PaginationParams
  ): Promise<{
    practitioners: Practitioner[];
    pagination: PaginationParams;
  }> {
    const params = new URLSearchParams();
    if (pagination.page) params.set('page', pagination.page.toString());
    if (pagination.limit) params.set('limit', pagination.limit.toString());
    if (pagination.status) params.set('status', pagination.status);
    const response = await api.get(`/clinic/${clinicId}/practitioners?${params}`);
    return response.data;
  }

  // Get clinic prescriptions
  async getPrescriptions(
    clinicId: string,
    page = 1,
    limit = 20
  ): Promise<{
    prescriptions: Prescription[];
    pagination: any;
  }> {
    const response = await api.get(`/clinic/${clinicId}/prescriptions?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Get clinic transactions
  async getTransactions(
    clinicId: string,
    page = 1,
    limit = 20,
    search = '',
    startDate = '',
    endDate = ''
  ): Promise<{
    transactions: Transaction[];
    pagination: any;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
    });

    const response = await api.get(`/clinic/${clinicId}/transactions?${params}`);
    return response.data;
  }

  // Get clinic settlements
  async getSettlements(clinicId: number): Promise<any[]> {
    const response = await api.get(`/clinic/${clinicId}/settlements`);
    return response.data;
  }

  // Get payment overview/metrics
  async getPaymentOverview(
    clinicId: string, // Changed from number to string to accept UUID
    timePeriod?: string,
    startDate = '',
    endDate = ''
  ): Promise<PaymentOverview> {
    const params = new URLSearchParams({
      ...(timePeriod && { timePeriod }),
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
    });

    const response = await api.get(`/clinic/${clinicId}/payment-overview?${params}`);
    return response.data;
  }

  // Save new patient
  async savePatient(patientData: any): Promise<Patient> {
    const response = await api.post('/clinic/save-patient', patientData);
    return response.data;
  }

  // Get patient by ID
  async getPatientById(patientId: number): Promise<Patient> {
    const response = await api.get(`/clinic/patients/${patientId}`);
    return response.data;
  }

  // Update patient
  async updatePatient(patientId: number, patientData: any): Promise<Patient> {
    const response = await api.put(`/clinic/patients/${patientId}`, patientData);
    return response.data;
  }

  // Get patient prescriptions
  async getPatientPrescriptions(
    patientId: number,
    page = 1,
    limit = 20
  ): Promise<{
    prescriptions: Prescription[];
    pagination: any;
  }> {
    const response = await api.get(
      `/clinic/patients/${patientId}/prescriptions?page=${page}&limit=${limit}`
    );
    return response.data;
  }

  async getClinic(clinicId: string): Promise<ClinicContext> {
    const response = await api.get(`/clinic/${clinicId}`);
    return response.data;
  }

  // Dashboard API methods
  async getDashboardMetrics(clinicId: string): Promise<DashboardMetrics> {
    const response = await api.get(`/clinic/${clinicId}/dashboard/metrics`);
    return response.data?.data || response.data;
  }

  async getDashboardGrowth(clinicId: string, months = 6): Promise<{ growthData: DashboardGrowthData[] }> {
    const response = await api.get(`/clinic/${clinicId}/dashboard/growth?months=${months}`);
    return response.data?.data || response.data;
  }

  async getDashboardActivity(clinicId: string, days = 7, limit = 20): Promise<{ activities: DashboardActivity[] }> {
    const response = await api.get(`/clinic/${clinicId}/dashboard/activity?days=${days}&limit=${limit}`);
    return response.data?.data || response.data;
  }

  async updateClinicInfo(
    clinicId: string,
    data: {
      name?: string;
      address_line_1?: string;
      address_line_2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      primary_phone?: string;
      fax?: string;
      email?: string;
      description?: string;
    }
  ): Promise<{ success: boolean; data: { clinic: Clinic } }> {
    const response = await api.put(`/clinic/${clinicId}/info`, data);
    return response.data;
  }

  async acknowledgeProgram(
    clinicId: string
  ): Promise<{ success: boolean; data: { clinic: Clinic } }> {
    const response = await api.post(`/clinic/${clinicId}/acknowledge-program`);
    return response.data;
  }
}

export const clinicService = new ClinicService();
export default clinicService;
